import { UIIconProps } from "common/blocks/UIIcon";
import { EventNames, useSub } from "hooks/usePublishSubscribe";
import { useState, useRef, useCallback } from "react";

export interface IUseSidebar {
    openSidebar: (sidebar: Sidebar, params?: { [key: string]: any }) => void;
    openSecondSidebar: (sidebar: Sidebar, params?: { [key: string]: any }) => void;
    closeSidebar: (avoidCallback?: boolean) => void;
    closeSecondSidebar: () => void;
    goBack: () => void;
    sidebarContent: Sidebar | undefined;
    secondSidebarContent: Sidebar | undefined;
    registerOnCloseCallback: (cb: Function) => void;
    registerSidebars: (popups: RegisteredSidebars) => void;
    openRegisteredSidebar: (
        id: string,
        params?: { [key: string]: any },
        globalParams?: { [key: string]: any }
    ) => void;
    setSidebarTitle: (title: string) => void;
    isSidebarOpen: boolean;
}

export type Sidebar = {
    title?: string;
    titleIcon?: UIIconProps;
    previousSidebar?: Sidebar;
    closingModalGoesToPrevious?: boolean;
    disableScroll?: boolean;
    content: {
        component: any;
        footerComponent?: any;
        props?: { [key: string]: any };
    };
};

export type RegisteredSidebars = {
    [id: string]: Sidebar;
};

const useSidebar = (): IUseSidebar => {
    const [sidebarContent, setSidebarContent] = useState<Sidebar | undefined>();
    const [secondSidebarContent, setSecondSidebarContent] = useState<Sidebar | undefined>();
    const [registeredSidebars, setRegisteredSidebars] = useState<RegisteredSidebars>({});
    const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

    const onCloseCallBack = useRef<Function | null>(null);

    const closeSidebar = (avoidCallBack?: boolean) => {
        if (!!sidebarContent?.closingModalGoesToPrevious && sidebarContent.previousSidebar) {
            setSidebarContent(sidebarContent.previousSidebar);
            setSecondSidebarContent(undefined);
        } else {
            setIsSidebarOpen(false);
            setSidebarContent(undefined);
            setSecondSidebarContent(undefined);
        }

        if (avoidCallBack) return;

        if (onCloseCallBack.current) {
            onCloseCallBack.current();
        }
    };

    const closeSecondSidebar = () => setSecondSidebarContent(undefined);

    const openSecondSidebar = (sidebar: Sidebar, params: { [key: string]: any } = {}) => {
        if (!sidebar || !sidebarContent) {
            throw new Error(
                "Impossible to open the second layer's sidebar while the first layer is empty"
            );
        }

        setSecondSidebarContent({
            ...sidebar,
            content: {
                ...sidebar.content,
                props: {
                    ...sidebar.content.props,
                    params,
                },
            },
        });
    };
    // CONSUME event_openSidebar events
    const openSidebarWithPubSub = useSub(EventNames.OpenSidebar, (data) => {
        const { sidebar, params } = data;
        setSidebarContent({
            ...sidebar,
            content: {
                ...sidebar.content,
                props: {
                    ...sidebar.content.props,
                    params,
                },
            },
        });
        setIsSidebarOpen(true);
        return;
    });

    const openSidebar = (sidebar: Sidebar, params: { [key: string]: any } = {}) => {
        // use modal context way of open/close
        setSidebarContent({
            ...sidebar,
            content: {
                ...sidebar.content,
                props: {
                    ...sidebar.content.props,
                    params,
                },
            },
        });
        setIsSidebarOpen(true);
    };

    const setSidebarTitle = useCallback(
        (title: string) => {
            if (sidebarContent) {
                setSidebarContent({
                    ...sidebarContent,
                    title,
                });
            }
        },
        [sidebarContent]
    );

    const registerOnCloseCallback = (cb: Function) => {
        onCloseCallBack.current = cb;
    };

    const goBack = () => {
        if (sidebarContent?.previousSidebar) setSidebarContent(sidebarContent.previousSidebar);
    };

    const openRegisteredSidebar = (
        id: string,
        params: { [key: string]: any } = {},
        globalParams: { [key: string]: any } = {}
    ) => {
        if (!registeredSidebars[id]) throw new Error(`No registered modals with id: ${id}`);

        setIsSidebarOpen(true);
        setSidebarContent({
            ...registeredSidebars[id],
            content: {
                ...registeredSidebars[id].content,
                props: {
                    ...registeredSidebars[id].content.props,
                    params,
                },
            },
            ...globalParams,
        });
    };

    const registerSidebars = (sidebars: RegisteredSidebars) =>
        setRegisteredSidebars((alreadyRegistered) => Object.assign(alreadyRegistered, sidebars));

    return {
        closeSidebar,
        openSidebar,
        goBack,
        sidebarContent,
        registerOnCloseCallback,
        registerSidebars,
        openRegisteredSidebar,
        setSidebarTitle,
        secondSidebarContent,
        closeSecondSidebar,
        openSecondSidebar,
        isSidebarOpen,
    } as IUseSidebar;
};

export default useSidebar;
